import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { AttendancePopover } from "@/components/classroom-roster/AttendancePopover";
import { formatDate } from "@/lib/helpers";
import { ArrowLeft, MapPin, Clock, User, Printer, Users } from "lucide-react";
import { toast } from "sonner";

export default function ClassroomRosterPage() {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/classrooms/${classroomId}/roster`);
      setRoster(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load roster");
    } finally {
      setLoading(false);
    }
  }, [classroomId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveAttendance = async (studentId, status, note) => {
    try {
      await api.put(`/classrooms/${classroomId}/attendance/${studentId}`, { status, note });
      toast.success(status ? "Attendance updated" : "Attendance cleared");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update attendance");
      throw err;
    }
  };

  if (loading) return <Skeleton className="h-96 rounded-[var(--radius)]" />;

  if (error || !roster) {
    return (
      <div className="rounded-[var(--radius)] border bg-card p-8 text-center space-y-3" data-testid="roster-error-state">
        <p className="text-sm text-muted-foreground">{error || "Roster not found."}</p>
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="roster-error-back-button">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classroom-roster-page">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="roster-back-button">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Link to={`/classrooms/${classroomId}/roster/print`} target="_blank" rel="noreferrer">
          <Button variant="outline" size="sm" data-testid="roster-print-button">
            <Printer className="h-4 w-4 mr-2" /> Print Roster
          </Button>
        </Link>
      </div>

      <div>
        <h1 className="font-heading text-2xl font-semibold">{roster.class_name}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-1.5">
          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {roster.location || "-"}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {roster.start_time} - {roster.end_time}</span>
          <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {roster.instructor_name || "Unassigned"}</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {(roster.dates || []).map((d) => (
            <span key={d} className="text-xs bg-secondary rounded-full px-3 py-1">{formatDate(d)}</span>
          ))}
        </div>
      </div>

      <div className="rounded-[var(--radius)] border bg-card">
        <div className="p-4 sm:p-5 border-b flex items-center justify-between">
          <h2 className="font-semibold font-heading flex items-center gap-2">
            <Users className="h-4 w-4" /> Roster ({roster.students.length})
          </h2>
          {!roster.can_edit_attendance && (
            <span className="text-xs text-muted-foreground">View only</span>
          )}
        </div>

        {roster.students.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No students enrolled in this session yet.</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="roster-table">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="p-3 font-medium">Student</th>
                    <th className="p-3 font-medium">Attendance</th>
                    <th className="p-3 font-medium">Payment</th>
                    <th className="p-3 font-medium">Forms</th>
                    <th className="p-3 font-medium">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {roster.students.map((s) => (
                    <tr key={s.student_id} data-testid="roster-student-row">
                      <td className="p-3">
                        <button onClick={() => navigate(`/students/${s.student_id}`)} className="font-medium hover:text-[hsl(var(--role-accent))]">
                          {s.full_name}
                        </button>
                      </td>
                      <td className="p-3">
                        <AttendancePopover
                          status={s.attendance_status}
                          note={s.attendance_note}
                          canEdit={roster.can_edit_attendance}
                          onSave={(status, note) => saveAttendance(s.student_id, status, note)}
                        />
                      </td>
                      <td className="p-3"><StatusBadge status={s.payment_status} /></td>
                      <td className="p-3"><StatusBadge status={s.forms_status} /></td>
                      <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">{s.attendance_note || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y">
              {roster.students.map((s) => (
                <div key={s.student_id} className="p-4 space-y-2" data-testid="roster-student-card">
                  <button onClick={() => navigate(`/students/${s.student_id}`)} className="font-medium text-sm hover:text-[hsl(var(--role-accent))]">
                    {s.full_name}
                  </button>
                  <div className="flex items-center gap-2 flex-wrap">
                    <AttendancePopover
                      status={s.attendance_status}
                      note={s.attendance_note}
                      canEdit={roster.can_edit_attendance}
                      onSave={(status, note) => saveAttendance(s.student_id, status, note)}
                    />
                    <StatusBadge status={s.payment_status} />
                    <StatusBadge status={s.forms_status} />
                  </div>
                  {s.attendance_note && <p className="text-xs text-muted-foreground">{s.attendance_note}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
